/**
 * Users admin handler (Casdoor)
 * Manages Casdoor users with JWT-based auth
 */

import { useCallback, useState } from "react";
import type { authenticationTypes } from "@pineseed/spec";
import type { UseAdminAppReturn } from "../useAdminApp";

type CasdoorUser = authenticationTypes["CasdoorUser"];

type AuthConfig = {
  casdoor: {
    serverUrl: string;
    clientId: string;
    appName: string;
    organizationName: string;
    redirectPath: string;
    signinPath?: string;
    ui: {
      mode: 'redirect' | 'embedded' | 'popup' | 'silent';
      theme?: 'light' | 'dark' | 'auto';
      customization?: Record<string, unknown>;
    };
    sso?: {
      enabled: boolean;
      providers?: string[];
    };
    providerConfig?: Record<string, unknown>;
  };
};

export interface UsersAdminHandler {
  entities: CasdoorUser[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createEntity: (data: Partial<CasdoorUser>) => Promise<void>;
  updateEntity: (userId: string, data: Partial<CasdoorUser>) => Promise<void>;
  deleteEntity: (userId: string) => Promise<void>;
  getStats: () => { total: number };
  authenticateWithPopup: () => Promise<string>; // Returns JWT
}

export function useUsersAdmin(
  adminApp: UseAdminAppReturn,
  authConfig: AuthConfig
): UsersAdminHandler {
  const [entities, setEntities] = useState<CasdoorUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Authenticate and get JWT via popup
  const authenticateWithPopup = useCallback(async (): Promise<string> => {
    try {
      // Generate API key to get JWT in the process
      // We'll extract the JWT from the auth flow
      const { AuthHandler } = await import('@pineseed/workspace/handlers/auth-handler');
      const authHandler = AuthHandler.getInstance(authConfig);
      
      await authHandler.initialize({
        casdoorConfig: authConfig.casdoor,
      });
      
      const loginUrl = await authHandler.getLoginUrl();
      
      const result = await new Promise<{ jwt: string }>((resolve, reject) => {
        const popup = window.open(
          loginUrl,
          'CasdoorUserAuth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        
        if (!popup) {
          reject(new Error('Popup blocked. Please allow popups for this site.'));
          return;
        }
        
        const handleMessage = async (event: MessageEvent) => {
          if (!event.origin.includes('localhost') && !event.origin.includes(window.location.hostname)) {
            return;
          }
          
          if (event.data?.type === 'CASDOOR_AUTH_CALLBACK') {
            window.removeEventListener('message', handleMessage);
            clearTimeout(timeoutId);
            
            const { code, error: authError, state } = event.data;
            
            try {
              popup.close();
            } catch (e) {
              // Ignore COOP errors
            }
            
            if (authError) {
              reject(new Error(`Auth error: ${authError}`));
              return;
            }
            
            if (code) {
              try {
                const authResult = await authHandler.handleCallback(code, state || '');
                await authHandler.clearStoredAuthState();
                resolve({ jwt: authResult.tokens.accessToken });
              } catch (error) {
                reject(error);
              }
            }
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        const timeoutId = setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          try {
            if (popup && !popup.closed) popup.close();
          } catch (e) {
            // Ignore
          }
          reject(new Error('Login timeout'));
        }, 300000);
      });
      
      return result.jwt;
    } catch (error) {
      console.error('Authentication failed:', error);
      throw error;
    }
  }, [authConfig]);

  // Fetch all users
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const jwt = await authenticateWithPopup();
      const response = await fetch(`${adminApp.httpUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }
      
      const users = await response.json();
      setEntities(Array.isArray(users) ? users : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(message);
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, authenticateWithPopup]);

  // Create user
  const createEntity = useCallback(async (data: Partial<CasdoorUser>) => {
    try {
      const jwt = await authenticateWithPopup();
      const response = await fetch(`${adminApp.httpUrl}/api/user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create user: ${response.statusText}`);
      }
      
      await refetch();
    } catch (err) {
      console.error('Create user error:', err);
      throw err;
    }
  }, [adminApp.httpUrl, authenticateWithPopup, refetch]);

  // Update user
  const updateEntity = useCallback(async (userId: string, data: Partial<CasdoorUser>) => {
    try {
      const jwt = await authenticateWithPopup();
      const response = await fetch(`${adminApp.httpUrl}/api/user/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update user: ${response.statusText}`);
      }
      
      await refetch();
    } catch (err) {
      console.error('Update user error:', err);
      throw err;
    }
  }, [adminApp.httpUrl, authenticateWithPopup, refetch]);

  // Delete user
  const deleteEntity = useCallback(async (userId: string) => {
    try {
      const jwt = await authenticateWithPopup();
      const response = await fetch(`${adminApp.httpUrl}/api/user/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.statusText}`);
      }
      
      await refetch();
    } catch (err) {
      console.error('Delete user error:', err);
      throw err;
    }
  }, [adminApp.httpUrl, authenticateWithPopup, refetch]);

  // Get statistics
  const getStats = useCallback(() => {
    return {
      total: entities.length,
    };
  }, [entities]);

  return {
    entities,
    loading,
    error,
    refetch,
    createEntity,
    updateEntity,
    deleteEntity,
    getStats,
    authenticateWithPopup,
  };
}
